import { Box, Flex, NativeSelect, Stack, Tag, Text } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { LuArrowLeft, LuExternalLink, LuHistory } from 'react-icons/lu'
import { useTranslation } from '../../i18n'

function SidebarItem({ item, current, unsaved, onNavigate }) {
  const { t } = useTranslation()
  const Icon = item.icon
  const active = item.key === current
  return (
    <Flex
      as={RouterLink}
      to={item.to}
      onClick={(e) => onNavigate(e, item)}
      aria-current={active ? 'page' : undefined}
      align="center"
      gap={2.5}
      px={2.5}
      py={1.5}
      borderRadius="md"
      fontSize="sm"
      fontWeight={active ? '600' : '500'}
      color={active ? 'var(--mc-text)' : 'var(--mc-text-muted)'}
      bg={active ? 'var(--mc-bg-selected)' : 'transparent'}
      _hover={{ bg: active ? 'var(--mc-bg-selected)' : 'var(--mc-bg-raised)', color: 'var(--mc-text)' }}
      transition="background-color 0.15s ease, color 0.15s ease"
    >
      <Box as={Icon} boxSize="16px" flexShrink={0} aria-hidden="true" />
      <Text as="span" flex="1" truncate>{t(item.labelKey)}</Text>
      {item.external && <Box as={LuExternalLink} boxSize="13px" opacity={0.7} aria-hidden="true" />}
      {unsaved && (
        // Amber: unsaved changes are a warning state, not an action.
        <Box w="8px" h="8px" borderRadius="full" bg="var(--mc-amber)" flexShrink={0}>
          <Text as="span" srOnly>{t('workspace.unsavedMark')}</Text>
        </Box>
      )}
    </Flex>
  )
}

function GroupLabel({ children }) {
  return (
    <Text px={2.5} pb={1} fontSize="xs" fontWeight="600" color="var(--mc-text-subtle)">
      {children}
    </Text>
  )
}

/**
 * The event's own navigation: its pages first, then its settings tabs in their
 * groups. Desktop gets a sticky column; a phone gets the same list as one
 * native select, which is the control a phone already knows how to scroll.
 *
 * `onNavigate(e, item)` runs before every move, so the workspace can stop it
 * and ask about unsaved changes first.
 */
export function EventSidebar({ event, eventId, typeLabel, runItems, settingsGroups, current, unsavedKey, onNavigate, onSelect }) {
  const { t } = useTranslation()
  const allItems = [...runItems, ...settingsGroups.flatMap((g) => g.items)]

  return (
    <>
      {/* Phone */}
      <Stack gap={2} display={{ base: 'flex', lg: 'none' }}>
        <PhoneEventHeading event={event} typeLabel={typeLabel} onNavigate={onNavigate} />
        <NativeSelect.Root size="md">
          <NativeSelect.Field
            aria-label={t('workspace.sectionPicker')}
            value={current}
            onChange={(e) => onSelect(allItems.find((i) => i.key === e.target.value))}
          >
            <optgroup label={t('workspace.groupRun')}>
              {runItems.map((i) => (
                <option key={i.key} value={i.key}>{t(i.labelKey)}{i.external ? ' ↗' : ''}</option>
              ))}
            </optgroup>
            {settingsGroups.map((g) => (
              <optgroup key={g.id} label={t(g.labelKey)}>
                {g.items.map((i) => (
                  <option key={i.key} value={i.key}>{t(i.labelKey)}{i.key === unsavedKey ? ' •' : ''}</option>
                ))}
              </optgroup>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Stack>

      {/* Desktop */}
      <Stack
        as="nav"
        aria-label={t('workspace.navLabel')}
        display={{ base: 'none', lg: 'flex' }}
        gap={5}
        w="240px"
        flexShrink={0}
        position="sticky"
        top="88px"
        maxH="calc(100vh - 104px)"
        overflowY="auto"
        pr={1}
      >
        <EventHeading event={event} eventId={eventId} typeLabel={typeLabel} onNavigate={onNavigate} />

        <Stack gap={0.5}>
          <GroupLabel>{t('workspace.groupRun')}</GroupLabel>
          {runItems.map((item) => (
            <SidebarItem key={item.key} item={item} current={current} onNavigate={onNavigate} />
          ))}
        </Stack>

        {settingsGroups.map((g) => (
          <Stack key={g.id} gap={0.5}>
            <GroupLabel>{t(g.labelKey)}</GroupLabel>
            {g.items.map((item) => (
              <SidebarItem key={item.key} item={item} current={current} unsaved={item.key === unsavedKey} onNavigate={onNavigate} />
            ))}
          </Stack>
        ))}
      </Stack>
    </>
  )
}

// Leaving the event drops the workspace and its draft with it, so this link
// goes through the same unsaved-changes check as the sidebar items.
const ALL_EVENTS = { key: 'all-events', to: '/events' }
const classicItem = (eventId) => ({ key: 'classic', to: `/events/${eventId}/classic` })

// A phone's screen is for the page, so the heading is one line: the way back,
// the event's name and its type. The id and the classic page are desktop-only.
function PhoneEventHeading({ event, typeLabel, onNavigate }) {
  const { t } = useTranslation()
  return (
    <Flex align="center" gap={2} minW={0}>
      <Flex
        as={RouterLink}
        to="/events"
        onClick={(e) => onNavigate(e, ALL_EVENTS)}
        aria-label={t('workspace.allEvents')}
        align="center"
        justify="center"
        boxSize="32px"
        ml={-1.5}
        flexShrink={0}
        borderRadius="md"
        color="var(--mc-text-muted)"
        _hover={{ color: 'var(--mc-text)', bg: 'var(--mc-bg-raised)' }}
      >
        <LuArrowLeft aria-hidden="true" />
      </Flex>
      <Text flex="1" minW={0} fontSize="md" fontWeight="700" truncate>
        {event?.name || t('events.untitled')}
      </Text>
      {typeLabel && <Tag.Root size="sm" flexShrink={0}>{typeLabel}</Tag.Root>}
    </Flex>
  )
}

function EventHeading({ event, eventId, typeLabel, onNavigate }) {
  const { t } = useTranslation()
  return (
    <Stack gap={2} px={2.5}>
      <Flex
        as={RouterLink}
        to="/events"
        onClick={(e) => onNavigate(e, ALL_EVENTS)}
        align="center"
        gap={1.5}
        fontSize="sm"
        color="var(--mc-text-muted)"
        _hover={{ color: 'var(--mc-text)' }}
        alignSelf="flex-start"
      >
        <LuArrowLeft aria-hidden="true" /> {t('workspace.allEvents')}
      </Flex>
      <Text fontSize="lg" fontWeight="700" lineHeight="1.25" css={{ textWrap: 'balance' }}>
        {event?.name || t('events.untitled')}
      </Text>
      <Flex gap={1.5} wrap="wrap">
        <Tag.Root size="sm" fontFamily="mono" userSelect="all">{eventId}</Tag.Root>
        {typeLabel && <Tag.Root size="sm">{typeLabel}</Tag.Root>}
      </Flex>
      {/* The old single-page view, kept as a fallback. Quiet on purpose: the
          workspace is the default, and this is the way out if it is needed. */}
      <Flex
        as={RouterLink}
        to={classicItem(eventId).to}
        onClick={(e) => onNavigate(e, classicItem(eventId))}
        align="center"
        gap={1.5}
        fontSize="xs"
        color="var(--mc-text-subtle)"
        _hover={{ color: 'var(--mc-text)', textDecoration: 'underline' }}
        alignSelf="flex-start"
      >
        <LuHistory aria-hidden="true" /> {t('workspace.classicLink')}
      </Flex>
    </Stack>
  )
}
